import React, { useState, useMemo } from "react";
import {
  Card,
  Button,
  Form,
  Table,
  Container,
  Row,
  Col,
} from "react-bootstrap";
import axios from "axios";
import API from "../utils/api";
import swal from "sweetalert";
import { FaTrashAlt } from "react-icons/fa";
import * as XLSX from "xlsx";

const GeneradorDeConsulta = () => {
  const [tipo, setTipo] = useState("");
  const [estado, setEstado] = useState("Pendiente");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [resultados, setResultados] = useState([]);
  const [loading, setLoading] = useState(false);

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  // =============================
  // FORMATEAR FECHA (SIN DATE)
  // =============================
  const formatearFecha = (fecha) => {
    if (!fecha) return "-";
    return fecha.split("T")[0].split("-").reverse().join("/");
  };

  // =============================
  // ORDENAMIENTO
  // =============================
  const handleSort = (key) => {
    let direction = "asc";

    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    setSortConfig({ key, direction });
  };

  const resultadosOrdenados = useMemo(() => {
    if (!sortConfig.key) return resultados;

    const sorted = [...resultados].sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];

      if (sortConfig.key === "importe" || sortConfig.key === "numero" || sortConfig.key === "cuota") {
        valA = Number(valA);
        valB = Number(valB);
      }

      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [resultados, sortConfig]);

  // =============================
  // CONSULTA
  // =============================
  const ejecutarConsulta = async () => {
    try {
      setLoading(true);

      const params = {
        tipo,
        estado,
        fechaDesde: fechaDesde || undefined,
        fechaHasta: fechaHasta || undefined,
      };

      const res = await axios.get(
        `${API.urlAPI}generador/CuotasGenerales`,
        { params }
      );

      setResultados(res.data || []);
      setSortConfig({ key: null, direction: "asc" });

    } catch (err) {
      swal("Error", "No se pudo ejecutar la consulta", "error");
    } finally {
      setLoading(false);
    }
  };

  // =============================
  // ELIMINAR
  // =============================
  const quitarCuota = (idCuota) => {
    setResultados((prev) =>
      prev.filter((r) => r.idCuota !== idCuota)
    );
  };

  // =============================
  // TOTAL
  // =============================
  const calcularTotal = () =>
    resultadosOrdenados.reduce(
      (acc, r) => acc + Number(r.importe || 0),
      0
    );

  // =============================
  // EXPORTAR
  // =============================
  const exportarExcel = () => {
    if (!resultadosOrdenados.length) {
      swal("Sin datos", "No hay información para exportar", "warning");
      return;
    }

    const data = resultadosOrdenados.map((r) => ({
      "Fecha de Pago": formatearFecha(r.fechaPago),
      "Fecha de Vencimiento": formatearFecha(r.fechaVencimiento),
      Tipo: r.tipo,
      Número: r.numero,
      Cuota: r.cuota,
      Cliente: r.cliente,
      Estado: r.estado,
      Importe: r.importe,
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Cuotas");

    XLSX.writeFile(
      workbook,
      `Cuotas_${fechaDesde || "inicio"}_${fechaHasta || "fin"}.xlsx`
    );
  };

  // =============================
  // REGISTRAR
  // =============================
  const guardarConsulta = async () => {
    if (!resultadosOrdenados.length) {
      swal("Sin datos", "No hay cuotas para registrar", "warning");
      return;
    }

    const confirm = await swal({
      title: "¿Registrar pagos?",
      text: "Se registrarán estas cuotas como pendientes.",
      icon: "warning",
      buttons: true,
    });

    if (!confirm) return;

    try {
      setLoading(true);

      await axios.post(
        `${API.urlAPI}pagoscobradores/registrar-multiples`,
        { cuotas: resultadosOrdenados }
      );

      swal("Éxito", "Pagos registrados correctamente", "success");

      // 🔥 LIMPIAR TODO
      setResultados([]);
      setFechaDesde("");
      setFechaHasta("");
      setSortConfig({ key: null, direction: "asc" });

    } catch (error) {
      swal("Error", "No se pudieron registrar los pagos", "error");
    } finally {
      setLoading(false);
    }
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return "↕";
    return sortConfig.direction === "asc" ? "▲" : "▼";
  };

  return (
    <Container>
      <br />
      <Card>
        <Card.Header style={{ background: "#005187", color: "white" }}>
          <h3>Generador de Consulta de Cuotas</h3>
        </Card.Header>

        <Card.Body>

          <Row className="mb-3">
            <Col md={3}>
              <Form.Label>Tipo</Form.Label>
              <Form.Select value={tipo} onChange={(e) => setTipo(e.target.value)}>
                <option value="">Créditos + Ventas</option>
                <option value="prestamo">Créditos</option>
                <option value="venta">Ventas</option>
              </Form.Select>
            </Col>

            <Col md={3}>
              <Form.Label>Estado</Form.Label>
              <Form.Select value={estado} onChange={(e) => setEstado(e.target.value)}>
                <option>Pendiente</option>
                <option>Atrasado</option>
                <option>Proximo a vencer</option>
                <option>Todos</option>
              </Form.Select>
            </Col>

            <Col md={3}>
              <Form.Label>Desde</Form.Label>
              <Form.Control
                type="date"
                value={fechaDesde}
                onChange={(e) => setFechaDesde(e.target.value)}
              />
            </Col>

            <Col md={3}>
              <Form.Label>Hasta</Form.Label>
              <Form.Control
                type="date"
                value={fechaHasta}
                onChange={(e) => setFechaHasta(e.target.value)}
              />
            </Col>
          </Row>

          <div className="text-center mb-4">
            <Button onClick={ejecutarConsulta} disabled={loading}>
              {loading ? "Consultando..." : "Ejecutar consulta"}
            </Button>
          </div>

          {resultadosOrdenados.length > 0 && (
            <>
              <Table striped bordered hover responsive>
                <thead>
                  <tr style={{ background: "#5589c4", color: "white", cursor: "pointer" }}>
                    <th onClick={() => handleSort("fechaPago")}>
                      Fecha Pago {renderSortIcon("fechaPago")}
                    </th>
                    <th onClick={() => handleSort("fechaVencimiento")}>
                      Fecha Venc. {renderSortIcon("fechaVencimiento")}
                    </th>
                    <th onClick={() => handleSort("tipo")}>
                      Tipo {renderSortIcon("tipo")}
                    </th>
                    <th onClick={() => handleSort("numero")}>
                      Número {renderSortIcon("numero")}
                    </th>
                    <th onClick={() => handleSort("cuota")}>
                      Cuota {renderSortIcon("cuota")}
                    </th>
                    <th onClick={() => handleSort("cliente")}>
                      Cliente {renderSortIcon("cliente")}
                    </th>
                    <th onClick={() => handleSort("estado")}>
                      Estado {renderSortIcon("estado")}
                    </th>
                    <th onClick={() => handleSort("importe")}>
                      Importe {renderSortIcon("importe")}
                    </th>
                    <th onClick={() => handleSort("importe")}>
                      Pagado {renderSortIcon("importe")}
                    </th>
                    <th onClick={() => handleSort("importe")}>
                      Restante {renderSortIcon("importe")}
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {resultadosOrdenados.map((r) => (
                    <tr key={r.idCuota}>
                      <td>{formatearFecha(r.fechaPago)}</td>
                      <td>{formatearFecha(r.fechaVencimiento)}</td>
                      <td>{r.tipo}</td>
                      <td>{r.numero}</td>
                      <td>{r.cuota}</td>
                      <td>{r.cliente}</td>
                      <td>{r.estado}</td>
                      <td>${Number(r.importeOriginal).toLocaleString("es-AR")}</td>
                      <td>${Number(r.montoPagado).toLocaleString("es-AR")}</td>
                      <td>${Number(r.importe).toLocaleString("es-AR")}</td>
                      <td className="text-center">
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => quitarCuota(r.idCuota)}
                        >
                          <FaTrashAlt />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <div className="text-end fw-bold">
                Total: ${calcularTotal().toLocaleString("es-AR")}
              </div>

              <div className="text-center mt-3 d-flex justify-content-center gap-3">
                <Button variant="success" onClick={guardarConsulta}>
                  Generar registros de cobros
                </Button>
                <Button variant="outline-success" onClick={exportarExcel}>
                  Exportar a Excel
                </Button>
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default GeneradorDeConsulta;
